import {
  ParticipantDto,
  PatientInformingProjectionDto,
  PatientInformingStatus,
} from "../api-client/api";

/**
 * What the badge should render for a given appointment/timeslot:
 * - `none`   : show nothing.
 * - `error`  : show a warning/error icon (needs staff attention).
 * - `arrows` : show 1–2 directional arrows indicating the patient was informed
 *              of an earlier (`up`) or later (`down`) time than the current one.
 */
export type InformingDisplay =
  | { kind: "none" }
  | { kind: "error" }
  | { kind: "arrows"; direction: "up" | "down"; count: 1 | 2 };

export interface DisplayConfig {
  /** Minutes represented by a single arrow. */
  intervalMinutes: number;
  /** Drift beyond this (= 2 × interval, i.e. >2 arrows) renders as an error. */
  toleranceMinutes: number;
}

/** Address.type value (AddressType.PRACTICE) that requires informing the patient. */
const PRACTICE_VISIT_TYPE = "Praxis";

/**
 * Arrow granularity / error tolerance by visit type:
 * - Praxis: stricter — 1 arrow per 5 min, error past 10 min. Applies to LATER
 *   starts only; any earlier start is an error (see computeInformingDisplay).
 * - Haus / Heim: looser — 1 arrow per 15 min, error past 30 min, in both
 *   directions.
 */
export const PRACTICE_CONFIG: DisplayConfig = { intervalMinutes: 5, toleranceMinutes: 10 };
export const HOME_CONFIG: DisplayConfig = { intervalMinutes: 15, toleranceMinutes: 30 };

/** True when the visit type is a practice visit (case-insensitive). */
export function isPracticeVisit(visitType: string | null | undefined): boolean {
  return (visitType ?? "").toLowerCase() === PRACTICE_VISIT_TYPE.toLowerCase();
}

export function configForVisitType(visitType: string | null | undefined): DisplayConfig {
  return isPracticeVisit(visitType) ? PRACTICE_CONFIG : HOME_CONFIG;
}

/**
 * PINNED design decision (2026-06-25): when the status is INFORMED_BUT_CHANGED
 * but the start time did NOT move (only location or therapist changed), there is
 * no time drift to render as arrows. Until the product decision is made we
 * surface it as an error so a real change is not silently hidden. Flip this to
 * `{ kind: "none" }` if the decision is to only flag time changes.
 */
const CHANGED_WITHOUT_TIME_DRIFT: InformingDisplay = { kind: "error" };

function minutesBetween(a: Date, b: Date): number {
  return (a.getTime() - b.getTime()) / 60000;
}

/**
 * Map an informing projection to what the badge should render.
 *
 * @param projection   The (already worst-case-aggregated) projection for the
 *                      appointment, or the per-slot projection. `null` => none.
 * @param currentStart The current start time of the appointment/slot, used to
 *                      measure drift against `projection.informedForStart`.
 * @param visitType    The raw `appointment.address.type` ("Praxis" | "Haus" |
 *                      "Heim"). Drives both the not-informed rule and the arrow
 *                      granularity (Praxis 5/10, Haus/Heim 15/30).
 */
export function computeInformingDisplay(
  projection: PatientInformingProjectionDto | null | undefined,
  currentStart: string | Date | null | undefined,
  visitType: string | null | undefined,
): InformingDisplay {
  if (!projection) return { kind: "none" };

  // Not-informed: every practice visit needs informing. A Haus/Heim visit only
  // when the server marks it as the patient's next one after an attended
  // appointment (PatientInformingProjectionDto.isUninformedNextVisit); Haus/Heim
  // visits further out render nothing, as does a missing field.
  if (projection.status === PatientInformingStatus.NotInformed) {
    if (isPracticeVisit(visitType)) return { kind: "error" };
    return projection.isUninformedNextVisit ? { kind: "error" } : { kind: "none" };
  }

  // Informed / InformedButChanged: render from the ACTUAL time drift between the
  // current start and the start the patient was told — not the server status
  // alone. Deriving it here makes the badge update OPTIMISTICALLY the instant a
  // card is dragged: `currentStart` moves with the drag's optimistic cache patch,
  // so the arrows/error appear immediately, before the server recomputes the
  // projection. In steady state a server-confirmed "Informed" always has
  // start == informedForStart (drift 0 => nothing), so this is a no-op for it.
  const informedFor = projection.informedForStart
    ? new Date(projection.informedForStart)
    : null;
  const current = currentStart ? new Date(currentStart) : null;

  // No reference time to measure against: a bare "changed" (e.g. location/
  // therapist only) still needs attention; a bare "informed" shows nothing.
  if (!informedFor || !current) {
    return projection.status === PatientInformingStatus.InformedButChanged
      ? { kind: "error" }
      : { kind: "none" };
  }

  const drift = minutesBetween(current, informedFor); // + later, - earlier
  const magnitude = Math.abs(drift);
  if (magnitude === 0) {
    // Start matches what the patient was told. If the server still flags it as
    // changed, that's a non-time change (location/therapist) => attention.
    return projection.status === PatientInformingStatus.InformedButChanged
      ? CHANGED_WITHOUT_TIME_DRIFT
      : { kind: "none" };
  }

  // Praxis, moved EARLIER than the informed time: never just a small shift. The
  // patient plans around the time they were told, so they would arrive after
  // their slot has already started — it always needs a fresh call, whatever the
  // magnitude. Praxis therefore never renders up-arrows. Haus/Heim visits are a
  // therapist-side route change, so an earlier start is treated like any other
  // drift and keeps the 15/30 thresholds in both directions.
  if (drift < 0 && isPracticeVisit(visitType)) return { kind: "error" };

  const { intervalMinutes, toleranceMinutes } = configForVisitType(visitType);
  if (magnitude > toleranceMinutes) return { kind: "error" };

  // Arrows mark COMPLETED intervals (one calendar shift = 5 min). A drift
  // smaller than a full interval is within the acceptable shift band and
  // shows no arrow — e.g. a 5- or 10-min move on a Haus/Heim visit (interval
  // 15) renders nothing, the first arrow appears at 15 min, the second once
  // the drift passes 15 (up to the 30-min tolerance). Praxis (interval 5)
  // behaves the same: 1 arrow at 5 min, 2 arrows past 5 up to 10.
  if (magnitude < intervalMinutes) return { kind: "none" };

  const count = (magnitude <= intervalMinutes ? 1 : 2) as 1 | 2;
  return { kind: "arrows", direction: drift < 0 ? "up" : "down", count };
}

/** Severity ordering for aggregating multiple participants' displays. */
function displaySeverity(display: InformingDisplay): number {
  if (display.kind === "error") return 3;
  if (display.kind === "arrows") return display.count >= 2 ? 2 : 1;
  return 0;
}

/**
 * Pick the single worst-case informing projection across a group appointment's
 * participants (KGG: up to 3 patients on one shared time slot). Each participant
 * carries its own per-slot projection (`participant.timeSlots[0].informing`) and
 * they can differ (e.g. one re-informed, another not). Since the group moves as
 * one, `currentStart` is shared; only what each patient was told differs.
 *
 * Returns the projection with the most severe display (error > 2-arrows >
 * 1-arrow), so a single {@link InformingBadge} on the KGG card surfaces the
 * patient that most needs attention. Returns `null` when no participant warrants
 * a badge (all informed-and-consistent, or none informed on a non-practice
 * visit). KGG is always a Praxis visit, so pass `"Praxis"` as `visitType`.
 */
export function pickWorstInformingProjection(
  participants: ParticipantDto[] | null | undefined,
  currentStart: string | Date | null | undefined,
  visitType: string | null | undefined,
): PatientInformingProjectionDto | null {
  if (!participants?.length) return null;

  let best: { projection: PatientInformingProjectionDto; severity: number } | null = null;
  for (const participant of participants) {
    const projection = participant.timeSlots?.[0]?.informing;
    if (!projection) continue;
    const severity = displaySeverity(
      computeInformingDisplay(projection, currentStart, visitType),
    );
    if (severity === 0) continue;
    if (!best || severity > best.severity) best = { projection, severity };
  }
  return best?.projection ?? null;
}
